'use client';

import { configKeepData5Mins } from '@/config/useQuery';
import { api } from '@/trpc/client';
import { Download } from 'lucide-react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import PublicRatingSection from './public-rating-section';
import PublicUnitTable from './public-unit-table';
import StatHeroBanner from './stat-hero-banner';

// ─── Lazy-load ECharts ────────────────────────────────────────────────────────

const PublicDonutChart = dynamic(() => import('./public-donut-chart'), {
    ssr: false,
    loading: () => (
        <div className='flex h-[280px] items-center justify-center'>
            <div className='h-28 w-28 animate-pulse rounded-full border-[16px] border-gray-100' />
        </div>
    ),
});

const PublicBarChart = dynamic(() => import('./public-bar-chart'), {
    ssr: false,
    loading: () => (
        <div className='h-[280px] animate-pulse rounded-xl bg-gray-50' />
    ),
});

const PublicLineChart = dynamic(() => import('./public-line-chart'), {
    ssr: false,
    loading: () => (
        <div className='h-[280px] animate-pulse rounded-xl bg-gray-50' />
    ),
});

const PublicReturnChart = dynamic(() => import('./public-return-chart'), {
    ssr: false,
    loading: () => (
        <div className='flex h-[280px] items-center justify-center'>
            <div className='h-28 w-28 animate-pulse rounded-full border-[16px] border-gray-100' />
        </div>
    ),
});

// ─── Shared UI ────────────────────────────────────────────────────────────────

const ChartCard = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <div className='overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100'>
        <div className='flex items-center justify-between border-b border-gray-50 px-5 py-3.5'>
            <p className='text-sm font-semibold text-gray-700'>{title}</p>
        </div>
        <div className='p-4'>{children}</div>
    </div>
);

// ─── Tab definitions ──────────────────────────────────────────────────────────

type TabId = 'overview' | 'methods' | 'unit-quality' | 'rating';

// ─── Main component ───────────────────────────────────────────────────────────

const PublicStatisticsManagement = () => {
    const transPage = useTranslations('PublicStatistics');
    const now = new Date();
    const [activeTab, setActiveTab] = useState<TabId>('overview');

    // ── Summary data — gọi 1 lần, chia sẻ cho hero + donut ───────────────────
    const { data: summaryData, isLoading: isSummaryLoading } =
        api.dashboard.Query_GetPublicSummary.useQuery({}, configKeepData5Mins);

    // ── Monthly data — chia sẻ cho bar + line chart ───────────────────────────
    const { data: monthlyData, isLoading: isMonthlyLoading } =
        api.dashboard.Query_GetPublicMonthlyBreakdown.useQuery(
            {},
            configKeepData5Mins
        );

    const summary = summaryData?.data ?? null;
    const monthly = monthlyData?.data ?? null;

    const TABS: { id: TabId; label: string }[] = [
        { id: 'overview', label: transPage('PublicStatistics_TabOverview') },
        { id: 'methods', label: transPage('PublicStatistics_TabMethods') },
        {
            id: 'unit-quality',
            label: transPage('PublicStatistics_TabUnitQuality'),
        },
        { id: 'rating', label: transPage('PublicStatistics_TabRating') },
    ];

    return (
        <div className='min-h-screen bg-slate-50/70'>
            {/* Hero banner — luôn hiển thị */}
            <div className='p-4 pb-0 lg:p-5 lg:pb-0'>
                <StatHeroBanner
                    summary={summary}
                    isLoading={isSummaryLoading}
                />
            </div>

            {/* Tab nav — sticky dưới hero */}
            <div className='sticky top-0 z-10 bg-slate-50/95 px-4 pb-0 pt-3 backdrop-blur-sm lg:px-5'>
                <div className='scrollbar-none flex gap-1 overflow-x-auto rounded-2xl bg-white p-1 shadow-sm ring-1 ring-gray-100'>
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={[
                                'flex-shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition-all',
                                activeTab === tab.id
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                            ].join(' ')}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab content */}
            <div className='space-y-4 p-4 pt-4 lg:p-5'>
                {/* ── Tab 1: Tổng quan ── */}
                {activeTab === 'overview' && (
                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                        <ChartCard
                            title={transPage(
                                'PublicStatistics_ProcessingStatus'
                            )}
                        >
                            <PublicDonutChart
                                summary={summary}
                                isLoading={isSummaryLoading}
                            />
                        </ChartCard>
                        <ChartCard
                            title={transPage('PublicStatistics_MonthlyTrend')}
                        >
                            <PublicLineChart
                                monthly={monthly}
                                isLoading={isMonthlyLoading}
                            />
                        </ChartCard>
                    </div>
                )}

                {/* ── Tab 2: Phương thức ── */}
                {activeTab === 'methods' && (
                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                        <ChartCard
                            title={transPage(
                                'PublicStatistics_SubmissionMethod'
                            )}
                        >
                            <PublicBarChart
                                monthly={monthly}
                                isLoading={isMonthlyLoading}
                            />
                        </ChartCard>
                        <ChartCard
                            title={transPage('PublicStatistics_ReturnMethod')}
                        >
                            <PublicReturnChart />
                        </ChartCard>
                    </div>
                )}

                {/* ── Tab 3: Chất lượng đơn vị ── */}
                {activeTab === 'unit-quality' && (
                    <div className='overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100'>
                        <PublicUnitTable />
                    </div>
                )}

                {/* ── Tab 4: Đánh giá dịch vụ ── */}
                {activeTab === 'rating' && <PublicRatingSection />}

                {/* Footer */}
                <div className='flex flex-col items-start justify-between gap-3 rounded-2xl bg-white px-5 py-4 shadow-sm ring-1 ring-gray-100 sm:flex-row sm:items-center'>
                    <div>
                        <p className='text-xs text-gray-400'>
                            {transPage('PublicStatistics_LastUpdated')}:{' '}
                            <span className='font-medium text-gray-600'>
                                {now.toLocaleDateString('vi-VN', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </span>
                        </p>
                        <p className='mt-0.5 text-[11px] text-gray-400'>
                            {transPage('PublicStatistics_DataNote')}
                        </p>
                    </div>
                    <button
                        disabled
                        className='flex cursor-not-allowed items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-400'
                        title='Coming soon'
                    >
                        <Download size={13} />
                        {transPage('PublicStatistics_ExportReport')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublicStatisticsManagement;
